"use client";

import { useRef } from "react";
import Link from "next/link";
import { motion, useScroll, useTransform } from "motion/react";
import { useTranslation } from "@/lib/i18n/context";

/* Position of the "First full block of deep work…" highlight pill inside the
   journal screenshot frame, measured as % of the frame box (see PR notes).
   pill: left 27% · right 52% · top 84% · bottom 88% → ring padded slightly. */
const RING = { left: "25.5%", top: "74.5%", width: "28.5%", height: "15.5%" };

function FrameShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="relative w-full max-w-[1120px] mx-auto rounded-[20px] border overflow-hidden"
      style={{
        aspectRatio: "2.25 / 1",
        background: "var(--paper-2)",
        borderColor: "var(--hair)",
        boxShadow: "0 1px 2px rgba(26,24,21,0.04), 0 30px 70px rgba(26,24,21,0.10)",
      }}
    >
      {/* Top bezel chrome */}
      <div
        className="absolute top-0 left-0 right-0 h-7 flex items-center px-3 gap-1.5 border-b z-20"
        style={{ borderColor: "var(--hair)", background: "var(--paper-2)" }}
      >
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
        <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
      </div>
      <img
        src="/landing/journal.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover object-top pt-7"
      />
      {children}
    </div>
  );
}

/* The callout: terracotta ring around the pill + a side label.
   `t` is the i18n journalSection block; the four motion props drive the
   scroll-scrubbed reveal (on mobile they are plain numbers = fully shown). */
function Annotation({
  j,
  scrim,
  ring,
  ringScale,
  label,
  labelX,
}: {
  j: ReturnType<typeof useTranslation>["t"]["landing"]["cover"]["journalSection"];
  scrim: any;
  ring: any;
  ringScale: any;
  label: any;
  labelX: any;
}) {
  return (
    <>
      {/* Scrim to push focus toward the highlight.
         NOTE: journal.png is a permanently-light screenshot regardless of theme,
         so this overlay uses literal light-brand colors (NOT --ink / --paper,
         which invert in dark mode and would lighten the image / hide the label). */}
      <motion.div
        className="absolute inset-0 pt-7 z-10 pointer-events-none"
        style={{ background: "#1A1815", opacity: scrim }}
      />

      {/* Ring around the highlight pill */}
      <motion.div
        className="absolute z-10 rounded-[10px] pointer-events-none"
        style={{
          ...RING,
          opacity: ring,
          scale: ringScale,
          border: "1.5px solid var(--terracotta)",
          boxShadow: "0 0 0 4px color-mix(in srgb, var(--terracotta) 14%, transparent), 0 8px 24px rgba(184,83,26,0.18)",
        }}
      />

      {/* Side label, upper-right of the pill */}
      <motion.div
        className="absolute z-10 flex flex-col gap-1 pointer-events-none"
        style={{ left: "56%", top: "70%", opacity: label, x: labelX }}
      >
        {/* Fixed colors: this label overlays the always-light journal.png, so it
           must not invert with the theme. Terracotta is theme-stable; the italic
           uses literal light-brand ink (#1A1815) for contrast on the screenshot. */}
        <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-[var(--terracotta)]">
          ✦ {j.annotationTag}
        </span>
        <span className="font-display italic text-[clamp(15px,1.5vw,19px)] leading-snug" style={{ color: "#1A1815" }}>
          {j.annotationLabel}
        </span>
      </motion.div>
    </>
  );
}

export function JournalSection() {
  const { t } = useTranslation();
  const j = t.landing.cover.journalSection;
  const pinRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: pinRef,
    offset: ["start start", "end end"],
  });

  // Mobile pin: the native card sticks for ~1 extra screen while the highlight
  // pill scrubs in — a small "pause" at the archive section, mirroring desktop.
  const mobilePinRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: mProg } = useScroll({
    target: mobilePinRef,
    offset: ["start start", "end end"],
  });
  const mPill = useTransform(mProg, [0.12, 0.45], [0, 1]);
  const mPillY = useTransform(mProg, [0.12, 0.45], [14, 0]);
  const mRing = useTransform(mProg, [0.12, 0.45], [0.9, 1]);

  const scrim = useTransform(scrollYProgress, [0.12, 0.4], [0, 0.12]);
  const ring = useTransform(scrollYProgress, [0.18, 0.45], [0, 1]);
  const ringScale = useTransform(scrollYProgress, [0.18, 0.45], [1.08, 1]);
  const label = useTransform(scrollYProgress, [0.3, 0.55], [0, 1]);
  const labelX = useTransform(scrollYProgress, [0.3, 0.55], [-12, 0]);

  return (
    <section className="relative max-w-[1320px] mx-auto px-5 md:px-12 pt-20 md:pt-[120px] z-[2]">
      {/* Copy */}
      <div className="max-w-[760px] mx-auto text-left md:text-center">
        <div className="kicker-mono inline-flex items-center gap-2.5 mb-6">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--terracotta)]" /> {j.kicker}
        </div>
        <h2
          className="font-display text-[clamp(34px,4.4vw,56px)] leading-[1.05] tracking-[-0.02em] text-balance"
          style={{ fontWeight: 380 }}
        >
          {j.h2a} <em className="text-[var(--terracotta)] italic">{j.h2em}</em>
        </h2>
        <p className="mt-7 text-[17px] md:text-[19px] leading-[1.65] text-[var(--ink-soft)] text-pretty">
          {j.description}
        </p>
        <p className="mt-8 font-display italic text-[clamp(18px,1.7vw,23px)] leading-[1.5] text-[var(--ink)] text-balance">
          {j.growth}
        </p>
      </div>

      {/* Desktop / tablet: scroll-pinned frame with scrubbed annotation (≥ lg) */}
      <div ref={pinRef} className="hidden lg:block relative" style={{ height: "180vh" }}>
        <div className="sticky top-0 h-screen flex items-center">
          <div className="w-full">
            <FrameShell>
              <Annotation
                j={j}
                scrim={scrim}
                ring={ring}
                ringScale={ringScale}
                label={label}
                labelX={labelX}
              />
            </FrameShell>
          </div>
        </div>
      </div>

      {/* Mobile: native mini journal-entry card (< lg) — the desktop screenshot
         is unreadable at ~340px, so we reconstruct the moment natively:
         Fraunces title, mono date badge, terracotta highlight pill. The card
         pins for ~1 extra screen so the user lingers on the archive moment. */}
      <div ref={mobilePinRef} className="lg:hidden relative mt-10" style={{ height: "140vh" }}>
        <div className="sticky top-[72px] flex min-h-[calc(100vh-72px)] items-center pb-10">
        <div
          className="w-full rounded-[18px] border overflow-hidden"
          style={{
            background: "var(--paper)",
            borderColor: "var(--hair)",
            boxShadow: "0 1px 2px rgba(26,24,21,0.07), 0 14px 36px rgba(26,24,21,0.07)",
          }}
        >
          {/* Bezel chrome */}
          <div
            className="flex items-center gap-1.5 border-b px-3.5 py-2.5"
            style={{ borderColor: "var(--hair)" }}
          >
            <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
            <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
            <span className="w-2 h-2 rounded-full" style={{ background: "var(--rule)" }} />
            <span className="ml-auto font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
              {j.kicker}
            </span>
          </div>

          <div className="p-5">
            {/* Date line — mono, like the real journal */}
            <div className="font-display italic text-[15px] text-[var(--ink-soft)]">
              {j.cardDate}
            </div>
            {/* Highlight pill — the moment the AI surfaced (scrubs in on scroll) */}
            <motion.span
              className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-[var(--terracotta)]/35 bg-[var(--terracotta)]/[0.08] px-2.5 py-1"
              style={{ opacity: mPill, y: mPillY, scale: mRing }}
            >
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[var(--terracotta)]">
                ✦ {j.annotationTag}
              </span>
            </motion.span>
            {/* The entry highlight + body — taken from the demo journal */}
            <motion.p
              className="mt-3 font-display text-[21px] leading-[1.3] text-[var(--ink)] text-balance"
              style={{ opacity: mPill, y: mPillY }}
            >
              {j.cardEntry}
            </motion.p>
            <p className="mt-3 text-[15px] leading-[1.6] text-[var(--ink-soft)] text-pretty">
              {j.cardBody}
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Centered "More about journal" link — shows below the desktop pinned
         frame and below the mobile card (single shared placement). */}
      <div className="mt-8 md:mt-10 flex justify-center">
        <Link
          href="/product/journal"
          className="inline-flex items-center font-mono text-[12px] tracking-[0.16em] uppercase text-[var(--terracotta)] underline-offset-4 hover:underline"
        >
          {`${j.learnMore} →`}
        </Link>
      </div>
    </section>
  );
}
